import React, { useState, useEffect } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronUp, ChevronDown, Trash2, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TriggerRule, PostVariableMapping, Question } from "@/types/theme";
import { TriggerConditionsSection } from "@/components/builder/trigger/TriggerConditionsSection";
import { TriggerWebhookSection } from "@/components/builder/trigger/TriggerWebhookSection";
import { TriggerVariableMappingSection } from "@/components/builder/trigger/TriggerVariableMappingSection";
import { TriggerProviderVariableMappingSection } from "@/components/builder/trigger/TriggerProviderVariableMappingSection";
import { TriggerInput } from "@/components/builder/trigger/TriggerInput";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";

interface TriggerRuleItemProps {
  rule: TriggerRule;
  isOpen: boolean;
  localRuleName: string;
  questions: Question[];
  onToggle: () => void;
  onDelete: () => void;
  onSaveRule: (ruleId: string, updatedRule: Partial<TriggerRule>) => void;
  onAddCondition: () => void;
  localConditionValues: Record<string, string>;
  onUpdateCondition: (index: number, updatedCondition: Partial<any>) => void;
  onDeleteCondition: (index: number) => void;
  reportUnsavedChanges: (isDirty: boolean) => void;
}

export const TriggerRuleItem: React.FC<TriggerRuleItemProps> = ({
  rule,
  isOpen,
  localRuleName,
  questions,
  onToggle,
  onDelete,
  onSaveRule,
  onAddCondition,
  localConditionValues,
  onUpdateCondition,
  onDeleteCondition,
  reportUnsavedChanges,
}) => {
  // Local state to track form changes without immediately saving
  const [nameValue, setNameValue] = useState(localRuleName);
  const [notesValue, setNotesValue] = useState(rule.notes || '');
  const [webhookUrlValue, setWebhookUrlValue] = useState(rule.webhookUrl || '');
  const [variableMappings, setVariableMappings] = useState<{[key: string]: string}>({});
  const [providerVariableMappings, setProviderVariableMappings] = useState<{[key: string]: {[key: string]: string}}>({});
  const [selectedProviders, setSelectedProviders] = useState<Array<{id: string; url: string; name: string}>>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize variable mappings from rule
  useEffect(() => {
    // Initialize global mappings (no providerId)
    const mappings: {[key: string]: string} = {};
    const providerMappings: {[key: string]: {[key: string]: string}} = {};

    rule.postVariableMappings?.forEach(mapping => {
      if (mapping.providerId) {
        // Provider-specific mapping
        if (!providerMappings[mapping.providerId]) {
          providerMappings[mapping.providerId] = {};
        }
        providerMappings[mapping.providerId][mapping.questionId] = mapping.variableName || '';
      } else {
        // Global mapping
        mappings[mapping.questionId] = mapping.variableName || '';
      }
    });

    setVariableMappings(mappings);
    setProviderVariableMappings(providerMappings);

    // Initialize other form values
    setNameValue(localRuleName);
    setNotesValue(rule.notes || '');
    setWebhookUrlValue(rule.webhookUrl || '');
    setHasUnsavedChanges(false);
  }, [rule, localRuleName]);

  // Track if any values have changed
  useEffect(() => {
    const nameChanged = nameValue !== localRuleName;
    const notesChanged = notesValue !== (rule.notes || '');
    const webhookUrlChanged = webhookUrlValue !== (rule.webhookUrl || '');

    // Check variable mappings
    let mappingsChanged = false;
    const currentMappings = rule.postVariableMappings || [];

    // Check global mappings
    for (const questionId in variableMappings) {
      const existingMapping = currentMappings.find(m => m.questionId === questionId && !m.providerId);
      if (!existingMapping && variableMappings[questionId]) {
        mappingsChanged = true;
        break;
      }
      if (existingMapping && existingMapping.variableName !== variableMappings[questionId]) {
        mappingsChanged = true;
        break;
      }
    }

    // Check provider-specific mappings
    if (!mappingsChanged) {
      for (const providerId in providerVariableMappings) {
        for (const questionId in providerVariableMappings[providerId]) {
          const existingMapping = currentMappings.find(
            m => m.questionId === questionId && m.providerId === providerId
          );
          if (!existingMapping && providerVariableMappings[providerId][questionId]) {
            mappingsChanged = true;
            break;
          }
          if (existingMapping && existingMapping.variableName !== providerVariableMappings[providerId][questionId]) {
            mappingsChanged = true;
            break;
          }
        }
        if (mappingsChanged) break;
      }
    }

    // If any existing mappings are not in our local state, that's a change too
    if (!mappingsChanged) {
      for (const mapping of currentMappings) {
        if (mapping.providerId) {
          // Provider-specific mapping
          if (!providerVariableMappings[mapping.providerId] ||
              providerVariableMappings[mapping.providerId][mapping.questionId] === undefined) {
            mappingsChanged = true;
            break;
          }
        } else {
          // Global mapping
          if (variableMappings[mapping.questionId] === undefined) {
            mappingsChanged = true;
            break;
          }
        }
      }
    }

    setHasUnsavedChanges(nameChanged || notesChanged || webhookUrlChanged || mappingsChanged);
    reportUnsavedChanges(nameChanged || notesChanged || webhookUrlChanged || mappingsChanged);
  }, [nameValue, notesValue, webhookUrlValue, variableMappings, providerVariableMappings, rule, localRuleName]);

  // Handle variable name changes
  const handleVariableNameChange = (questionId: string, value: string, providerId?: string) => {
    if (providerId) {
      // Provider-specific mapping
      setProviderVariableMappings(prev => {
        // Create a new object to avoid mutating the previous state
        const newState = { ...prev };

        // Initialize the provider object if it doesn't exist
        if (!newState[providerId]) {
          newState[providerId] = {};
        }

        // Set the value for this question
        newState[providerId] = {
          ...newState[providerId],
          [questionId]: value
        };

        return newState;
      });
    } else {
      // Global mapping
      setVariableMappings(prev => ({
        ...prev,
        [questionId]: value
      }));
    }
    reportUnsavedChanges(true);
  };

  // Helper function to get variable name for a question
  const getVariableName = (questionId: string, providerId?: string): string => {
    if (providerId) {
      // Provider-specific mapping
      return providerVariableMappings[providerId]?.[questionId] || '';
    } else {
      // Global mapping
      return variableMappings[questionId] || '';
    }
  };

  // Parse selected providers from webhookUrl
  useEffect(() => {
    if (!webhookUrlValue) {
      setSelectedProviders([]);
      return;
    }

    try {
      // Try to parse as JSON array of provider URLs
      const parsed = JSON.parse(webhookUrlValue);
      if (Array.isArray(parsed) && parsed.every(p => p.id && p.url)) {
        // Map providers, ensuring they have names
        const providersWithNames = parsed.map(p => ({
          id: p.id,
          url: p.url,
          name: p.name || 'Unknown Provider'
        }));
        setSelectedProviders(providersWithNames);
        return;
      }
    } catch (e) {
      // Not JSON, single URL or invalid
    }

    setSelectedProviders([]);
  }, [webhookUrlValue]);

  // Helper function to validate webhook URL (handles both custom URLs and PingPost provider selections)
  const isValidWebhookUrl = (url: string): boolean => {
    if (!url) return true; // Empty URLs are valid (webhook is optional)

    // Check if it's a PingPost provider selection (JSON array)
    try {
      const parsed = JSON.parse(url);
      if (Array.isArray(parsed) && parsed.every(p => p.id && p.url)) {
        // It's a valid PingPost provider selection - validate each URL
        return parsed.every(p => isValidSingleUrl(p.url));
      }
    } catch (e) {
      // Not JSON, continue with single URL validation
    }

    // Validate as a single URL (could be custom URL or single provider URL)
    return isValidSingleUrl(url);
  };

  // Helper function to validate a single URL
  const isValidSingleUrl = (url: string): boolean => {
    if (!url) return true; // Empty URLs are valid

    // Trim and check for spaces
    const trimmedUrl = url.trim();
    if (trimmedUrl !== url || trimmedUrl.includes(' ')) {
      return false;
    }

    try {
      // Use URL constructor for validation
      const urlObj = new URL(url);

      // Verify protocol is http or https
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return false;
      }

      const hostname = urlObj.hostname;

      // Make sure hostname doesn't have spaces
      if (hostname.includes(' ')) {
        return false;
      }

      // Check for double dots (invalid domain)
      if (hostname.includes('..')) {
        return false;
      }

      // Allow localhost and IP addresses for development
      if (hostname === 'localhost' ||
          hostname === '127.0.0.1' ||
          /^192\.168\./.test(hostname) ||
          /^10\./.test(hostname) ||
          /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)) {
        return true;
      }

      // For other hostnames, ensure they have at least one dot (valid domain)
      if (!hostname.includes('.')) {
        return false;
      }

      return true;
    } catch (error) {
      // If URL constructor throws an error, the URL is invalid
      return false;
    }
  };

  // Validate POST variable names
  const isValidVariableName = (name: string): boolean => {
    if (!name) return true; // Empty names are valid (will be omitted)

    // Variable names should follow snake_case pattern
    // Allow letters, numbers, and underscores
    // Must start with a letter
    // No spaces allowed
    const validPattern = /^[a-z][a-z0-9_]*$/;
    return validPattern.test(name);
  };

  // Save all changes
  const handleSaveChanges = () => {
    const trimmedName = nameValue.trim();
    if (!trimmedName) {
      toast({
        title: "Name required",
        description: "Please provide a name for this trigger rule.",
        variant: "destructive",
      });
      return;
    }

    // Validate webhook URL if provided
    const trimmedWebhookUrl = webhookUrlValue.trim();
    if (trimmedWebhookUrl && !isValidWebhookUrl(trimmedWebhookUrl)) {
      toast({
        title: "Invalid webhook URL",
        description: "Please enter a valid URL starting with http:// or https:// and cannot contain spaces",
        variant: "destructive",
      });
      return;
    }

    // Prepare the updated rule object with all changes
    const updatedRule: Partial<TriggerRule> = {};

    // Only include fields that have changed
    if (trimmedName !== (rule.name || '')) {
      updatedRule.name = trimmedName;
    }

    const trimmedNotes = notesValue.trim();
    if (trimmedNotes !== (rule.notes || '')) {
      updatedRule.notes = trimmedNotes;
    }

    // Use the already defined trimmedWebhookUrl
    if (trimmedWebhookUrl !== (rule.webhookUrl || '')) {
      updatedRule.webhookUrl = trimmedWebhookUrl;
    }

    // Validate variable names before saving
    const invalidVariables = Object.keys(variableMappings)
      .filter(questionId => {
        const value = variableMappings[questionId].trim();
        return value !== '' && !isValidVariableName(value);
      });

    if (invalidVariables.length > 0) {
      // Get the question texts to show in the error message
      const invalidQuestions = invalidVariables
        .map(id => questions.find(q => q.id === id)?.text || id)
        .join(', ');

      toast({
        title: "Invalid variable names",
        description: `Please fix the variable names for: ${invalidQuestions}. Use snake_case format (letters, numbers, underscores, starting with a letter).`,
        variant: "destructive",
      });
      return;
    }

    // Create post variable mappings array from our local state
    const postVariableMappings: PostVariableMapping[] = [];

    // Add global mappings
    Object.keys(variableMappings)
      .filter(questionId => variableMappings[questionId].trim() !== '')
      .forEach(questionId => {
        postVariableMappings.push({
          questionId,
          variableName: variableMappings[questionId].trim()
        });
      });

    // Add provider-specific mappings
    Object.keys(providerVariableMappings).forEach(providerId => {
      Object.keys(providerVariableMappings[providerId])
        .filter(questionId => providerVariableMappings[providerId][questionId].trim() !== '')
        .forEach(questionId => {
          postVariableMappings.push({
            questionId,
            variableName: providerVariableMappings[providerId][questionId].trim(),
            providerId
          });
        });
    });

    // Check if mappings have changed
    const originalMappings = rule.postVariableMappings || [];
    if (JSON.stringify(postVariableMappings.sort()) !== JSON.stringify([...originalMappings].sort())) {
      updatedRule.postVariableMappings = postVariableMappings;
    }

    // Only save if there are changes
    if (Object.keys(updatedRule).length > 0) {
      // Save all changes at once
      onSaveRule(rule.id, updatedRule);

      toast({
        title: "Changes saved",
        description: "All changes to the trigger rule have been saved.",
      });
    } else {
      toast({
        title: "No changes",
        description: "There were no changes to save.",
      });
    }

    setHasUnsavedChanges(false);
    reportUnsavedChanges(false);
  };

  // Discard changes and revert to original values
  const handleDiscardChanges = () => {
    setNameValue(localRuleName);
    setNotesValue(rule.notes || '');
    setWebhookUrlValue(rule.webhookUrl || '');

    // Reset variable mappings
    const mappings: {[key: string]: string} = {};
    const providerMappings: {[key: string]: {[key: string]: string}} = {};

    rule.postVariableMappings?.forEach(mapping => {
      if (mapping.providerId) {
        // Provider-specific mapping
        if (!providerMappings[mapping.providerId]) {
          providerMappings[mapping.providerId] = {};
        }
        providerMappings[mapping.providerId][mapping.questionId] = mapping.variableName || '';
      } else {
        // Global mapping
        mappings[mapping.questionId] = mapping.variableName || '';
      }
    });

    setVariableMappings(mappings);
    setProviderVariableMappings(providerMappings);

    setHasUnsavedChanges(false);
    reportUnsavedChanges(false);

    toast({
      title: "Changes discarded",
      description: "All changes have been discarded.",
    });
  };
  return (
    <Collapsible
      open={isOpen}
      onOpenChange={onToggle}
      className="border rounded-lg bg-white mb-4 overflow-hidden"
    >
      <div className="bg-gray-50 border-b">
        <div className="flex items-center justify-between px-4 py-3">
          <CollapsibleTrigger className="flex items-center flex-grow text-left">
            <div className="flex items-center">
              {isOpen ? (
                <ChevronUp className="h-4 w-4 mr-2" />
              ) : (
                <ChevronDown className="h-4 w-4 mr-2" />
              )}
              <div className="flex-grow">
                <span className="text-lg font-semibold">
                  {nameValue ? nameValue : <span className="text-gray-400">Untitled Rule</span>}
                </span>
              </div>
            </div>
          </CollapsibleTrigger>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <CollapsibleContent>
        <div className="p-4 space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Rule Name
            </label>
            <TriggerInput
              initialValue={nameValue}
              onSave={(value) => {
                setNameValue(value);
                reportUnsavedChanges(true);
              }}
              className="w-full text-sm p-2 border rounded"
              placeholder="Enter rule name"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Notes (optional)
            </label>
            <TriggerInput
              initialValue={notesValue}
              onSave={(value) => {
                setNotesValue(value);
                reportUnsavedChanges(true);
              }}
              className="w-full text-sm p-2 border rounded min-h-[80px]"
              placeholder="Add additional notes about this rule (not visible to users)"
              isTextarea={true}
              rows={4}
            />
          </div>

          {/* Save/Discard Changes Buttons */}
          {hasUnsavedChanges && (
            <div className="flex items-center justify-end space-x-2 py-2 px-3 bg-blue-50 rounded-md">
              <span className="text-sm text-blue-700 mr-2">You have unsaved changes</span>
              <Button
                size="sm"
                onClick={handleDiscardChanges}
                variant="outline"
                className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
              >
                <X className="h-4 w-4" />
                Discard
              </Button>
              <Button
                size="sm"
                onClick={handleSaveChanges}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </div>
          )}

          <TriggerConditionsSection
            rule={rule}
            questions={questions}
            onAddCondition={onAddCondition}
            localConditionValues={localConditionValues}
            onUpdateCondition={onUpdateCondition}
            onDeleteCondition={onDeleteCondition}
          />

          <Separator className="my-4" />

          <TriggerWebhookSection
            webhookUrl={webhookUrlValue}
            onWebhookUrlChange={(url) => {
              setWebhookUrlValue(url);
              reportUnsavedChanges(true);
            }}
          />

          <Separator className="my-4" />

          {selectedProviders.length > 0 ? (
            <TriggerProviderVariableMappingSection
              questions={questions}
              selectedProviders={selectedProviders}
              getVariableName={getVariableName}
              onVariableNameChange={handleVariableNameChange}
            />
          ) : (
            <TriggerVariableMappingSection
              questions={questions}
              getVariableName={(questionId) => getVariableName(questionId)}
              onVariableNameChange={(questionId, value) => {
                handleVariableNameChange(questionId, value);
                reportUnsavedChanges(true);
              }}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
